// ** React Imports
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Fab,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { shallowEqual, useSelector } from "react-redux";
import CommonSlider from "./CommonSlider";
import { useTranslation } from "react-i18next";
import i18n from "../../../modules/Auth/pages/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    padding: theme.spacing(2),
  },
  custom_box: {
    width: "100%",
    padding: theme.spacing(2),
    whiteSpace: "no-wrap",
    borderRadius: 10,
  },
}));

const CustomerInformation = (props) => {
  const {
    reservationData = {},
    closeModal,
    closeBackDrop,
    title,
    buttonText,
    buttonFunction,
    load,
    value,
    reservation_id,
    eventData,
    selectedLanguage,
    reservationIds,
  } = props;

  const { t } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage(selectedLanguage);
  }, [selectedLanguage]);

  const { hotel } = useSelector(
    ({ auth }) => ({
      hotel: auth.selectedHotel,
    }),
    shallowEqual
  );
  const hotelId = hotel.hotelId;
  const [selectedRooms, setSelectedRooms] = useState([
    reservationData.reservation_rooms,
  ]);
  const classes = useStyles();

  const returnParams = () => {
    if (value === "hold-booking") {
      return { hotelId: hotelId, bookingId: reservationData.booking_id };
    }
    if (value === "unassign-room") {
      return { hotelId: hotelId, reservationId: reservation_id };
    }
    if (eventData.event.extendedProps.locked_status === "UNLOCKED") {
      if (Object.keys(reservationIds).length > 1) {
        return {
          resIds: Object.keys(reservationIds)
            .filter((roomId) => selectedRooms.includes(roomId))
            .map((roomId) => reservationIds[roomId]),
          status: "LOCKED",
        };
      }
      return { reservationId: reservation_id, status: "LOCKED" };
    }

    if (eventData.event.extendedProps.locked_status === "LOCKED") {
      if (Object.keys(reservationIds).length > 1) {
        return {
          resIds: Object.keys(reservationIds)
            .filter((roomId) => selectedRooms.includes(roomId))
            .map((roomId) => reservationIds[roomId]),
          status: "UNLOCKED",
        };
      }
      return { reservationId: reservation_id, status: "UNLOCKED" };
    }
  };

  const handleChangeRooms = (e) => {
    setSelectedRooms(e.target.value);
  };

  return (
    <div className={classes.root}>
      <CommonSlider onBackdropClick={closeBackDrop} open={true}>
        <Grid container className={classes.textField}>
          <Grid item xs={10} alignSelf="center">
            <Typography variant="h5">{title} </Typography>
            {value === "hold-booking" && (
              <Typography variant="body1">
                Hold booking will release the room inventory
              </Typography>
            )}
          </Grid>
          <Grid item xs={2}>
            <IconButton sx={{ float: "right" }} onClick={closeModal}>
              <Close />
            </IconButton>
          </Grid>
        </Grid>
        <Grid container spacing={2} className={classes.textField}>
          <Grid item xs={12}>
            {value == "lock-booking" &&
              Object.keys(reservationIds).length > 1 && (
                <Box sx={{ width: "100%" }}>
                  <FormControl fullWidth>
                    <InputLabel>Select the room id</InputLabel>
                    <Select
                      multiple
                      value={selectedRooms}
                      onChange={handleChangeRooms}
                      renderValue={(selected) => selected.join(", ")}
                      label="Select the room id"
                    >
                      {Object.keys(reservationIds).map((roomId, idx) => (
                        <MenuItem key={idx} value={roomId}>
                          {roomId}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography
                    sx={{
                      py: 1.5,
                      px: 2,
                      fontWeight: "bold",
                      color: "white",
                      backgroundColor: "#36454F",
                      my: 2,
                      borderRadius: 1,
                    }}
                  >
                    {`${t("You are about to")} ${
                      eventData.event.extendedProps.locked_status !== "LOCKED"
                        ? `${t("lock")}`
                        : `${t("unlock")}`
                    } ${t("Room")} ${selectedRooms}`}
                  </Typography>
                </Box>
              )}

            {!reservationIds ||
              (Object.keys(reservationIds).length == 1 && (
                <Box
                  className={classes.custom_box}
                  bgcolor={"#36454F"}
                  style={{ color: "white" }}
                >
                  {value === "hold-booking" && (
                    <Typography>
                      {`${t("You are about to hold booking")} ${
                        reservationData?.booking_id
                      }`}
                    </Typography>
                  )}
                  {value === "unassign-room" && (
                    <Typography>
                      {`${t(
                        "You are about to un-assign current reservation in Room"
                      )} ${reservationData?.reservation_rooms}`}
                    </Typography>
                  )}

                  {value === "lock-booking" &&
                    Object.keys(reservationIds).length === 1 && (
                      <Typography>
                        {`${t("You are about to")} ${
                          eventData.event.extendedProps.locked_status !==
                          "LOCKED"
                            ? `${t("lock")}`
                            : `${t("unlock")}`
                        } ${t("Room")} ${reservationData.reservation_rooms}`}
                      </Typography>
                    )}
                </Box>
              ))}
          </Grid>
          {value === "hold-booking" && (
            <Grid item xs={12}>
              <Typography>{t("Do you want to continue?")}</Typography>
            </Grid>
          )}
        </Grid>
        <Grid container>
          <Grid
            item
            xs={12}
            container
            justifyContent="flex-end"
            className={classes.textField}
          >
            <Button
              type="submit"
              variant="outlined"
              style={{
                backgroundColor: `${"#36454F"}`,
                color: `${"white"}`,
                borderColor: "#36454F",
                fontWeight: "bold",
                textTransform: "none",
              }}
              onClick={() => {
                buttonFunction(returnParams());
              }}
              disabled={load}
            >
              {buttonText}
              {load && (
                <CircularProgress size={15} color="inherit" sx={{ ml: 1.5 }} />
              )}
            </Button>
          </Grid>
        </Grid>
      </CommonSlider>
    </div>
  );
};

export default CustomerInformation;
